import { useNavigate } from "react-router";

import { type ConversationResponse } from "@vega-api/generated/types/conversationResponse";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";

type Props = {
  conversation: ConversationResponse;
  to: string;
  testId: string;
  participantName?: string;
};

export const ConversationListItem = ({
  conversation,
  to,
  testId,
  participantName,
}: Props) => {
  const navigate = useNavigate();
  const latestMessage = conversation.latestMessage;
  const isUnread = conversation.unreadCount > 0;

  return (
    <button
      className="w-full rounded-lg border border-border/primary p-4 text-left hover:bg-background/secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default defaultTransition"
      data-testid={testId}
      data-unread={isUnread ? "true" : undefined}
      onClick={() => navigate(to)}
      type="button"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          {participantName ? (
            <span
              className={cnTwMerge(
                "body-base-medium text-text/primary",
                isUnread && "body-base-semibold",
              )}
            >
              {participantName}
            </span>
          ) : null}
          <Tag variant={TagVariants.Disabled}>{conversation.meta.dealName}</Tag>
        </div>
        <span className="inline-flex items-center gap-1.5 body-small-regular text-text/secondary shrink-0">
          {isUnread ? (
            <span
              aria-hidden="true"
              className="shrink-0 h-1.5 w-1.5 rounded-full bg-brand-default"
            />
          ) : null}
          {formatDateByPattern(
            latestMessage?.createdAt ?? conversation.updatedAt,
            DateFormatString.US_DATE_WITH_COMMA,
          )}
        </span>
      </div>
      <p
        className={cnTwMerge(
          "mt-2 body-small-regular text-text/secondary line-clamp-2",
          isUnread && "body-small-semibold text-text/primary",
        )}
      >
        {latestMessage?.messageContent}
      </p>
    </button>
  );
};
